// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AssetLogo, IconButton, Table, Translation } from '@shared/ui-kit';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { Tooltip } from '@mui/material';
import { useTopPositions } from './use-top-positions';

export const TopPositions = defineComponent({
  displayName: 'TopPositions',
  useSetup: useTopPositions,
  View: ({
    currency,
    topPositions,
    isOverflowing,
    onScrollContainer,
    onScrollLeft,
    onScrollRight,
    scrollContainerRef,
  }) => (
    <div className="mt-4 space-y-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-h3 font-medium text-graph-text">
          <Translation i18nKey="aggregator.top-positions" ns="aggregator" />
        </h2>
        <div className="flex items-center gap-1">
          <IconButton
            variant="icon"
            icon="ArrowLeft"
            size="xl"
            className="!pr-2.5"
            disabled={!isOverflowing.left}
            onClick={onScrollLeft}
            data-testid="top-positions-scroll-left"
          />
          <IconButton
            variant="icon"
            icon="ArrowRight"
            size="xl"
            className="!pl-2.5"
            disabled={!isOverflowing.right}
            onClick={onScrollRight}
            data-testid="top-positions-scroll-right"
          />
        </div>
      </div>

      <div className="-mx-1 min-w-0 px-1 pb-2">
        <div
          ref={scrollContainerRef}
          className="w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={onScrollContainer}
        >
          <div className="flex min-w-max gap-6">
            {topPositions.map(position => (
              <article
                key={position.id}
                className="w-64 min-w-64 max-w-64 flex-none rounded-lg bg-section p-4 text-graph-text"
              >
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-2">
                    <AssetLogo
                      assetType={position.assetType}
                      logoUrl={position.logoUrl}
                      className="mt-0.5 size-6 shrink-0 rounded-full"
                    />
                    <div className="min-w-0">
                      <Tooltip title={position.name} placement="top">
                        <p className="line-clamp-1 text-body font-semibold uppercase text-graph-text">
                          {position.name}
                        </p>
                      </Tooltip>
                      <p className="line-clamp-1 text-body text-primary-medium">
                        {position.symbol}
                      </p>
                    </div>
                  </div>
                  <span className="text-body text-primary-medium">
                    #{position.order}
                  </span>
                </div>

                <p className="money-display mb-2 text-h3 font-semibold text-graph-text">
                  <Table.Data
                    value={position.investment}
                    format="currency"
                    currency={currency}
                    container={false}
                  />
                </p>

                <div className="flex items-center justify-between gap-2 text-body font-semibold">
                  <Table.Data
                    value={position.periodReturnInvestment}
                    format="currency"
                    currency={currency}
                    formatOptions={{
                      signDisplay: 'exceptZero',
                    }}
                    validate
                  />
                  <Table.Data
                    value={position.periodReturn}
                    format="percentage"
                    formatOptions={{
                      signDisplay: 'exceptZero',
                    }}
                    validate
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  ),
});
